import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiService } from '../lib/services/api'
import { ADMIN_ENDPOINTS, USER_ENDPOINTS } from './endpoints'
import type { User } from '../lib/types'

// Response types
export interface UsersListResponse {
  users: User[]
  total: number
  page: number
  per_page: number
}

export interface UpdateUserData {
  first_name?: string
  last_name?: string
  display_name?: string
  language?: string
  timezone?: string
}

export interface CreateUserData {
  email: string
  first_name: string
  last_name: string
  display_name?: string
  password?: string
  role?: string
  language?: string
  timezone?: string
}

// Hook to fetch all users (with pagination)
export const useUsers = (page: number = 1, per_page: number = 20) => {
  return useQuery({
    queryKey: ['users', page, per_page],
    queryFn: async (): Promise<UsersListResponse> => {
      return apiService.get(`${USER_ENDPOINTS.LIST}?page=${page}&per_page=${per_page}`)
    },
  })
}

// Hook to fetch single user by ID
export const useUser = (userId: string) => {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: async (): Promise<User> => {
      return apiService.get(USER_ENDPOINTS.USER_BY_ID(userId))
    },
    enabled: !!userId,
  })
}

// Hook to update user
export const useUpdateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: string
      data: UpdateUserData
    }): Promise<User> => {
      return apiService.put(USER_ENDPOINTS.USER_BY_ID(userId), data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

// Hook to update user role (RBAC system - assigns roles by ID)
export const useUpdateUserRole = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      userId,
      roleIds,
    }: {
      userId: string
      roleIds: string[]
    }): Promise<void> => {
      return apiService.post(`/api/v1/rbac/users/${userId}/roles`, { role_ids: roleIds })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

// Hook to activate user
export const useActivateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string): Promise<User> => {
      return apiService.post(`${USER_ENDPOINTS.USER_BY_ID(userId)}/activate`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

// Hook to deactivate user
export const useDeactivateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string): Promise<User> => {
      return apiService.post(`${USER_ENDPOINTS.USER_BY_ID(userId)}/deactivate`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

// Hook to create user
export const useCreateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateUserData): Promise<User> => {
      return apiService.post(USER_ENDPOINTS.LIST, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

// Hook to delete user
export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      return apiService.delete(USER_ENDPOINTS.USER_BY_ID(userId))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

// Hook to change user password (admin only)
export const useChangeUserPassword = () => {
  return useMutation({
    mutationFn: async ({
      userId,
      newPassword,
    }: {
      userId: string
      newPassword: string
    }): Promise<void> => {
      return apiService.put(`${ADMIN_ENDPOINTS.USER_PASSWORD(userId)}`, {
        new_password: newPassword,
      })
    },
  })
}
